import {
  Button,
  CardHeaderRow,
  cn,
  FilingLine,
  RecordCard,
} from "@scribenote/brian-design-system";
import {
  type CardSeverity,
  SeverityBadge,
  SeverityReason,
  severityReaderChrome,
} from "@subjects/cardSeverity";
import { TaskAssigneeBadge } from "@tasks/TaskAssigneeBadge";
import type { Task } from "@tasks/tasksClient";
import { formatDateTime, formatPastDateTime } from "@utils/format-date";
import type { ReactNode } from "react";

/**
 * One task as a sheet of paper: what to do, who owns it, and the filing line
 * along the bottom. The default presentation, worn everywhere a task appears
 * outside the /tasks route: the subject pane's reader, the per-view task bell,
 * the notifications feed. /tasks writes the same task on an index
 * card instead (`TaskIndexCard`); the two share the data and the hooks, not the
 * paper.
 *
 * Severity is the card's condition rather than a line of type: `watch`/`urgent`
 * outline the sheet in their status ink and say the word under a highlighter
 * beside the title, with the agent's reason under it. A calm card is a clean
 * card.
 */
export const TaskCard = ({
  task,
  badge,
  actions,
  severity = "calm",
}: {
  task: Task;
  badge?: ReactNode;
  /** Resolution buttons (e.g. Mark done / Dismiss), rendered below the card body. */
  actions?: ReactNode;
  /** At-a-glance status (WEB-3195): ring + word under a wash + the reason. */
  severity?: CardSeverity;
}) => {
  const chrome = severityReaderChrome(severity);

  const trailing =
    badge || chrome ? (
      <span className="flex items-start gap-2">
        {badge}
        {chrome && (
          <SeverityBadge
            severity={severity}
            dataTestId={`task-severity-${task.id}`}
          />
        )}
      </span>
    ) : undefined;

  return (
    <RecordCard
      // A ring, not a border: the sheet's hairline is its edge, and status ink
      // belongs outside it. `chrome.ring` is spelled out in cardSeverity so
      // Tailwind sees both colors literally.
      className={cn(chrome && "ring-2", chrome?.ring)}
      data-testid={`task-card-${task.id}`}
    >
      <CardHeaderRow
        trailing={trailing}
        titleClassName="wrap-anywhere text-sm font-medium text-ink"
      >
        {task.title}
      </CardHeaderRow>
      {/* The reason is what the severity stands for, so it only appears with
          one: a calm card has nothing to explain. */}
      {chrome && task.reason && (
        <p className="mt-1">
          <SeverityReason reason={task.reason} />
        </p>
      )}
      <TaskAssigneeBadge task={task} className="mt-2" />
      <FilingLine className="mt-2">
        {task.dueAt && (
          <time dateTime={task.dueAt} title={formatDateTime(task.dueAt)}>
            Due {formatPastDateTime(task.dueAt)}
          </time>
        )}
        <time dateTime={task.createdAt} title={formatDateTime(task.createdAt)}>
          Raised {formatPastDateTime(task.createdAt)}
        </time>
        <span className="capitalize" data-testid={`task-status-${task.id}`}>
          {task.status.replace(/_/g, " ")}
        </span>
      </FilingLine>
      {actions && <div className="mt-2 flex flex-wrap gap-2">{actions}</div>}
    </RecordCard>
  );
};

/**
 * The two resolutions the backend accepts for a plain `human` to-do
 * (complete → done, dismiss → cancelled), shared by every surface that offers
 * them (notifications feed, subject dashboard).
 *
 * Neither is a headline act: finishing a to-do is a tick on paper, so Mark done
 * takes the outline and Dismiss, which only clears the card, the ghost. The same
 * pair the index card carries on /tasks.
 */
export const TaskResolutionActions = ({
  task,
  onComplete,
  onDismiss,
}: {
  task: Task;
  onComplete: () => void;
  onDismiss: () => void;
}) => (
  <>
    <Button
      variant="outline"
      size="sm"
      data-testid={`task-complete-${task.id}`}
      onClick={onComplete}
    >
      Mark done
    </Button>
    <Button
      variant="ghost"
      size="sm"
      data-testid={`task-dismiss-${task.id}`}
      onClick={onDismiss}
    >
      Dismiss
    </Button>
  </>
);
